/**
 * The Crystal Army hand-off loader.
 *
 * ── Why this is imperative DOM and not a React component ──
 *
 * The tile that starts the hand-off does not survive it. Its click handler ends with
 * `onNavigate?.()`, which on mobile closes the drawer and unmounts the whole Sidebar — so a
 * loader owned by that tile (state, portal, or otherwise) is torn down microseconds after it
 * appears. The flow then runs invisibly, which is the exact failure this loader exists to fix,
 * and it fails ONLY on mobile, where the hand-off is slowest and the reassurance matters most.
 *
 * Ownership therefore sits on `document.body`, outside React's tree entirely. The overlay
 * lives until it is dismissed or the page navigates away, regardless of what unmounts.
 *
 * It is also the reentrancy guard: `isPartnerHandoffRunning()` is true exactly while the
 * element is mounted, so a second click cannot start a second registration + sign-in.
 */

const ID = 'partner-handoff-overlay'

/**
 * Hard ceiling on how long the overlay may stay up.
 *
 * The sign-in's own budget is 20s, so anything past 30s means a promise never settled --
 * a webview suspended mid-request, a socket that hung. The overlay covers the viewport at
 * the top of the stacking order, so "never clears" means "the app is bricked until reload".
 * Expiring beats that in every case: the worst outcome is a loader that vanishes early.
 */
const MAX_MS = 30_000

let expiry: number | undefined
let startedAt = 0

/**
 * Step breadcrumbs for the hand-off.
 *
 * MetaMask's in-app browser has no devtools worth the name, so when this flow misbehaves on
 * a phone there is otherwise nothing to go on but "it went to the login page". Each step is
 * timestamped from the click, printed to the console, and kept in a buffer that can be read
 * back after the fact.
 *
 * They are also shown to the USER, in the overlay: a spinner that names what it is doing
 * ("Registering your wallet", "Signing you in") reads as progress, where a bare spinner for
 * fifteen seconds reads as a hang.
 */
const trail: string[] = []

export function partnerHandoffTrail(): string[] {
  return [...trail]
}

/*
 * Reachable from a console with no build tooling and no source maps.
 *
 * When a user reports "it went to the login page", asking them to run
 * `crystalArmyTrail()` returns the exact step it died on and how long each one took --
 * which is the difference between diagnosing this in one message and guessing at it.
 */
declare global {
  interface Window {
    crystalArmyTrail?: () => string[]
  }
}
if (typeof window !== 'undefined') window.crystalArmyTrail = partnerHandoffTrail

/** Record a step: log it, buffer it, and show it in the overlay. */
export function partnerHandoffStep(label: string, detail?: string): void {
  const ms = startedAt ? Date.now() - startedAt : 0
  const line = `+${String(ms).padStart(5)}ms  ${label}${detail ? ` -- ${detail}` : ''}`
  trail.push(line)
  // eslint-disable-next-line no-console
  console.info(`[crystal-army] ${line}`)

  const note = document.getElementById(`${ID}-note`)
  if (note) note.textContent = label
}

/** True while a hand-off is in flight. Used to reject a double click. */
export function isPartnerHandoffRunning(): boolean {
  return !!document.getElementById(ID)
}

/**
 * Show the loader. Idempotent — calling it twice does not stack overlays.
 *
 * Colours come from the theme's CSS custom properties rather than Tailwind classes, because
 * this element is built outside the JSX the compiler scans; a class string here would be
 * purged from the production stylesheet and the overlay would render unstyled.
 */
export function showPartnerHandoff(): void {
  if (isPartnerHandoffRunning()) return

  const root = document.createElement('div')
  root.id = ID
  root.setAttribute('role', 'status')
  root.setAttribute('aria-live', 'polite')
  root.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483647',
    'display:grid',
    'place-items:center',
    'padding:24px',
    'background:color-mix(in srgb, var(--color-canvas) 82%, transparent)',
    '-webkit-backdrop-filter:blur(6px)',
    'backdrop-filter:blur(6px)',
  ].join(';')

  const card = document.createElement('div')
  card.style.cssText = [
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'gap:14px',
    'padding:28px 32px',
    'border-radius:14px',
    'background:var(--surface-1)',
    'box-shadow:0 0 0 1px var(--color-line)',
    'max-width:320px',
    'text-align:center',
  ].join(';')

  const spinner = document.createElement('div')
  spinner.style.cssText = [
    'width:36px',
    'height:36px',
    'border-radius:50%',
    'border:3px solid var(--color-line)',
    'border-top-color:var(--color-accent)',
    'animation:partner-handoff-spin 0.8s linear infinite',
  ].join(';')

  // Respect a reduced-motion preference: the spin is decorative, the text is the message.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    spinner.style.animation = 'none'
  }

  const title = document.createElement('p')
  title.textContent = 'Opening Crystal Army'
  title.style.cssText = 'margin:0;font-size:14px;font-weight:600;color:var(--color-ink)'

  const note = document.createElement('p')
  note.id = `${ID}-note`
  note.textContent = 'Starting'
  note.style.cssText = 'margin:0;font-size:12px;line-height:1.45;color:var(--color-muted)'

  if (!document.getElementById(`${ID}-kf`)) {
    const kf = document.createElement('style')
    kf.id = `${ID}-kf`
    kf.textContent = '@keyframes partner-handoff-spin{to{transform:rotate(360deg)}}'
    document.head.appendChild(kf)
  }

  const hint = document.createElement('p')
  hint.textContent = 'This can take a few seconds the first time.'
  hint.style.cssText = 'margin:0;font-size:11px;line-height:1.45;color:var(--color-faint)'

  card.append(spinner, title, note, hint)
  root.appendChild(card)
  document.body.appendChild(root)

  // Escape hatch for a user who would rather not wait. Same reasoning as the timeout:
  // never trap someone behind this.
  root.addEventListener('click', () => hidePartnerHandoff())

  startedAt = Date.now()
  trail.length = 0
  partnerHandoffStep('Starting')

  window.clearTimeout(expiry)
  expiry = window.setTimeout(() => {
    partnerHandoffStep('Timed out', `no step completed within ${MAX_MS}ms`)
    hidePartnerHandoff()
  }, MAX_MS)
}

/**
 * Remove the loader.
 *
 * Only call this when the hand-off has actually given up and the user is staying on this
 * page. While any path is still heading for a redirect the loader must remain: hiding it
 * early is what makes the partner's login form appear out of a blank screen.
 */
export function hidePartnerHandoff(): void {
  window.clearTimeout(expiry)
  expiry = undefined
  document.getElementById(ID)?.remove()
}
